'use client';

import { useEffect, useState } from 'react';

import { BottomSheet, Text } from '@/design-system';
import { SheetTitle } from '@/design-system/shared';
import { useMarketCartQueryWithDefaultZipCode } from '../../../../api/market-cart/hooks/useMarketCartQueryWithDefaultZipCode';
import { SingleProductRecommendationType } from '../../../../api/single-product/dto/single-product-recommendation-type.enum';
import useSingleProductRecommendationQuery from '../../../../api/single-product/hooks/useSingleProductRecommendationQuery';
import UImage from '../../../../components/UImage';
import { useAddCartCompleteSheetStore } from '../../../../store/addCartCompleteSheetStore';
import { CartTab } from '../../../../type/cart';
import { SAFE_AREA_INSET_BOTTOM } from '../../../../utils/common/safe-area';
import { defaultItemThumbnail } from '../../../../utils/common/variables';
import { UstoreGtm } from '../../../../utils/gtm/ustore/ustoreGtm';
import { useHistoryMethod } from '../../../../utils/hook/useHistoryMethod';
import { CartPolicy } from '../../../../utils/policy/CartPolicy';
import { UstorePolicy } from '../../../../utils/policy/UstorePolicy';
import CartBadgeButton from '../../../subscription/components/CartBadgeButton';
import UstoreRecommendationScrollRow, {
  getRecommendationCardWidth,
} from '../UstoreRecommendationScrollRow';
import AlsoBoughtProductCard from './AlsoBoughtProductCard';

/** 담은 상품 썸네일 지름 */
const THUMBNAIL_SIZE_PX = 40;

/** 시트가 넓어질 수 있는 최대 폭 — 디자인시스템 BottomSheet가 이 폭에서 멈추고 가운데 정렬한다 */
const SHEET_MAX_WIDTH_PX = 480;

/** 시트 안 추천 카드 폭 — 시트가 화면보다 좁아질 수 있어 그 상한을 반영한다 */
const CARD_WIDTH = getRecommendationCardWidth(
  `min(100vw, ${SHEET_MAX_WIDTH_PX}px)`,
);

interface AlsoBoughtSheetProps {
  singleProductId: number;
  thumbnailUrl: string;
  /** 열림 여부 — 닫을 때 false로 내려 시트가 내려가는 애니메이션을 재생한다 */
  open: boolean;
  onClose: () => void;
}

/**
 * 담기 완료 바텀시트 본문
 *
 * 담기 시점에 함께 구매한 상품을 이미 받아둔 상태에서만 열리므로
 * 조회를 기다리는 화면이 없다. 추천이 없을 때의 담기 완료 토스트는 담기 쪽이 맡는다.
 */
const AlsoBoughtSheet = ({
  singleProductId,
  thumbnailUrl,
  open,
  onClose,
}: AlsoBoughtSheetProps) => {
  const history = useHistoryMethod();

  const { data: recommendedProducts } = useSingleProductRecommendationQuery({
    productId: singleProductId,
    type: SingleProductRecommendationType.ALSO_BOUGHT,
  });
  const { cartQuantity } = useMarketCartQueryWithDefaultZipCode();

  const products = recommendedProducts ?? [];

  const handleClickCart = () => {
    new UstoreGtm().click_cart();
    history.push(CartPolicy.getCartPath({ tab: CartTab.MARKET }));
    onClose();
  };

  if (products.length === 0) return null;

  return (
    <BottomSheet open={open} onClose={onClose} fitContent>
      {/* 카드가 좌우로 스크롤되는 줄이라 Body 기본 좌우 여백을 지우고 각 줄이 직접 여백을 갖는다 */}
      <BottomSheet.Body className="p-0">
        <div
          className="flex flex-col gap-[20px] pt-[20px]"
          // 아래 여백에 홈 인디케이터 영역을 더한다 — 시트가 화면 맨 아래에 붙어 그대로 두면 카드가 가린다
          style={{ paddingBottom: `calc(20px + ${SAFE_AREA_INSET_BOTTOM})` }}
        >
          <div className="flex items-center gap-[8px] px-[16px]">
            <UImage
              src={thumbnailUrl || defaultItemThumbnail}
              alt="담은 상품 썸네일"
              width={THUMBNAIL_SIZE_PX}
              height={THUMBNAIL_SIZE_PX}
              className="size-[40px] shrink-0 rounded-full object-cover"
            />
            <SheetTitle asChild>
              <Text
                variant="h5-bold-100"
                as="h2"
                className="min-w-0 flex-1 text-gray-800"
              >
                상품 담기 완료
              </Text>
            </SheetTitle>
            <CartBadgeButton
              cartItemCount={cartQuantity}
              onClick={handleClickCart}
            />
          </div>

          <div className="h-[1px] bg-gray-100" />

          <div className="flex flex-col gap-[12px]">
            <Text variant="h4-bold" as="h3" className="px-[16px] text-gray-800">
              {UstorePolicy.CART_ALSO_BOUGHT_CURATION}
            </Text>

            <UstoreRecommendationScrollRow>
              {products.map((product, index) => (
                <div
                  key={product.id}
                  className="shrink-0"
                  style={{ width: CARD_WIDTH }}
                >
                  <AlsoBoughtProductCard
                    product={product}
                    index={index}
                    curation={UstorePolicy.CART_ALSO_BOUGHT_CURATION}
                    onNavigate={onClose}
                  />
                </div>
              ))}
            </UstoreRecommendationScrollRow>
          </div>
        </div>
      </BottomSheet.Body>
    </BottomSheet>
  );
};

/**
 * 마켓 산지직배송 담기 완료 바텀시트
 *
 * 담기 버튼이 있던 화면은 담은 직후 사라지므로 화면 맨 바깥에 한 번만 올려두고,
 * store에 담긴 요청을 읽어 연다.
 */
const MarketAddCartCompleteSheet = () => {
  const request = useAddCartCompleteSheetStore.use.request();
  const setRequest = useAddCartCompleteSheetStore.use.setRequest();

  // 내려가는 애니메이션이 재생되는 동안에도 내용이 있어야 하므로,
  // 요청이 비워진 뒤에도 마지막 요청을 남겨 시트를 화면에 붙여둔다
  const [lastRequest, setLastRequest] = useState(request);

  useEffect(() => {
    if (request) setLastRequest(request);
  }, [request]);

  if (!lastRequest) return null;

  return (
    <AlsoBoughtSheet
      key={lastRequest.singleProductId}
      singleProductId={lastRequest.singleProductId}
      thumbnailUrl={lastRequest.thumbnailUrl}
      open={request !== null}
      onClose={() => setRequest(null)}
    />
  );
};

export default MarketAddCartCompleteSheet;
